import { NavIcon } from './shared';

// ─── Types ───

export interface ContactData {
  phone: string;
  email: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  emergencyName: string;
  emergencyRelationship: string;
  emergencyPhone: string;
}

// ─── Shared Styles ───

const sectionLabelStyle = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semi)',
  lineHeight: 'var(--line-height-sm)',
  letterSpacing: '0.3px',
  textTransform: 'uppercase' as const,
  color: 'var(--text-muted)',
};

const fieldLabelStyle = {
  fontSize: 'var(--font-size-sm)',
  lineHeight: 'var(--line-height-sm)',
  color: 'var(--text-placeholder)',
};

const fieldValueStyle = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-medium)',
  lineHeight: 'var(--line-height-base)',
  letterSpacing: '-0.15px',
  color: 'var(--text)',
};

// ─── Icon Container ───

function IconBox({ icon }: { icon: string }) {
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: 'var(--text-muted)',
      }}
    >
      <NavIcon name={icon} />
    </div>
  );
}

// ─── Field Row ───

function FieldRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
      <IconBox icon={icon} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={fieldLabelStyle}>{label}</span>
        <span style={fieldValueStyle}>{value}</span>
      </div>
    </div>
  );
}

// ─── Section Card ───

function SectionCard({
  label,
  children,
  variant = 'default',
}: {
  label: string;
  children: React.ReactNode;
  variant?: 'default' | 'warning';
}) {
  const isWarning = variant === 'warning';
  return (
    <div
      style={{
        background: isWarning
          ? 'linear-gradient(149deg, rgba(254, 243, 199, 0.3), rgba(254, 243, 199, 0.1))'
          : 'var(--bg-surface-2)',
        border: isWarning ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '17px',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
        }}
      >
        {isWarning && (
          <div style={{ color: 'var(--color-warning-text)', flexShrink: 0 }}>
            <NavIcon name="alert-triangle" />
          </div>
        )}
        <span
          style={{
            ...sectionLabelStyle,
            color: isWarning ? 'var(--color-warning-text)' : 'var(--text-muted)',
          }}
        >
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}

// ─── Main Component ───

export function ContactDetailView({
  contact,
  onEditContact,
}: {
  contact: ContactData;
  onEditContact: () => void;
}) {
  return (
    <>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-lg)',
            letterSpacing: '-0.31px',
            color: 'var(--text)',
          }}
        >
          Contact Information
        </span>
        <button
          onClick={onEditContact}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            height: 38,
            padding: '0 var(--sp-4)',
            borderRadius: 'var(--radius-full)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-medium)',
            lineHeight: 'var(--line-height-base)',
            letterSpacing: '-0.15px',
            color: 'var(--text-muted)',
            cursor: 'pointer',
          }}
        >
          <NavIcon name="edit" /> Edit Contact
        </button>
      </div>

      {/* Primary Contact */}
      <SectionCard label="Primary Contact">
        <FieldRow icon="phone" label="Mobile Phone" value={contact.phone} />
        <FieldRow icon="mail" label="Email" value={contact.email} />
      </SectionCard>

      {/* Address */}
      <SectionCard label="Address">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
          <IconBox icon="map-pin" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
            <span style={fieldValueStyle}>{contact.street}</span>
            <span
              style={{
                fontSize: 'var(--font-size-base)',
                lineHeight: 'var(--line-height-base)',
                color: 'var(--text-muted)',
              }}
            >
              {contact.city}, {contact.state} {contact.zip}
            </span>
          </div>
        </div>
      </SectionCard>

      {/* Emergency Contact */}
      <SectionCard label="Emergency Contact" variant="warning">
        {!contact.emergencyName && !contact.emergencyPhone ? (
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-placeholder)' }}>
            No emergency contact on file
          </span>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  lineHeight: 'var(--line-height-sm)',
                  color: 'var(--color-warning-text)',
                }}
              >
                Name
              </span>
              <span style={fieldValueStyle}>{contact.emergencyName || '—'}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  lineHeight: 'var(--line-height-sm)',
                  color: 'var(--color-warning-text)',
                }}
              >
                Relationship
              </span>
              <span style={fieldValueStyle}>{contact.emergencyRelationship || '—'}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  lineHeight: 'var(--line-height-sm)',
                  color: 'var(--color-warning-text)',
                }}
              >
                Phone
              </span>
              <span style={fieldValueStyle}>{contact.emergencyPhone || '—'}</span>
            </div>
          </div>
        )}
      </SectionCard>
    </>
  );
}
